/**
 * Pulse: rate the week privately; the couple sees only the combined direction, never the numbers.
 *
 * The rating is a face rather than a digit (Daylio, DailyBean): five tiles, rough to great, and the
 * tap IS the log — there is no Save button, because a weekly check-in that costs two presses is one
 * that gets skipped. The note stays optional underneath and saves on its own.
 *
 * Two readings sit under it, deliberately different shapes. MY weeks are a small line over the last
 * twelve with the face scale on the axis (MindDoc's mood graph), because a trend is what a private
 * record is for. OURS is a strip of hollow rings, one per complete week, tinted by direction and
 * empty inside (AutoSleep's week strip) — so a run of good weeks reads as a run without ever
 * putting a number on screen, which is the promise the shared view makes.
 *
 * ORDER MATTERS ON THIS SCREEN. The check-in is the reason the tab exists and it goes first; the
 * "Did it land?" question is a once-per-signal interruption and goes UNDER it, one at a time. It
 * used to be the other way round, which put the faces below the fold behind a stack of cards asking
 * about work the person had already finished thinking about.
 */
import { Panel, MEASURE_CLASS } from '@/components/panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { Face, FaceScale, faceFor } from '@/components/face-scale';
import { api } from '@/api';
import { agoLabel, localDay } from '@/lib/day';
import { cn } from '@/lib/utils';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { usePoll } from '@/usePoll';
import { useLayout } from '@/useLayout';
import { useStore } from '@/store';
import * as React from 'react';
import { View, type LayoutChangeEvent } from 'react-native';

interface PulseState {
  currentWeek: string;
  myThisWeek: { rating: number; note: string | null } | null;
  mine: { week: string; rating: number; note: string | null }[];
  shared: { week: string; direction: 'up' | 'down' | 'flat' }[];
  /**
   * Whether the other one has logged THIS week. A boolean and only ever a boolean — see the header
   * of `api/src/modules/pulse.ts` for why it is safe and what it may never become. The screen may
   * say that they logged; nothing on it may hint at what they said.
   */
  partnerLoggedThisWeek: boolean;
  /**
   * Signals whose plans finished a fortnight ago and were never asked about. Author-scoped: the
   * excerpt is your own words, shown back to the only person who ever saw them.
   */
  landing: { signalId: string; kind: string; excerpt: string; asked: string; since: string }[];
}

type Direction = 'up' | 'down' | 'flat';

/**
 * The direction colours, and the only palette in this file.
 *
 * The five FACE levels moved to `@/components/face-scale`, which is where the scale itself now
 * lives; these three directions stay because the shared strip is Pulse's alone. Same rule either
 * way: they are DATA — up means up in both colour modes — so `--pulse-up`, `--pulse-flat` and
 * `--pulse-down` are declared identically in light and dark in the hunsy theme block and are
 * absent from the Tuxedo table, which is how a direction keeps its meaning while every surface
 * around it restyles. The token arrives as a border CLASS because the set is three and fixed and
 * every name here is a literal, which is the one form NativeWind carries to native. Nothing
 * should add a fourth.
 */
const DIRECTIONS: Record<Direction, { edge: string; fill: string; word: string }> = {
  up: { edge: 'border-pulse-up', fill: 'bg-pulse-up', word: 'up' },
  flat: { edge: 'border-pulse-flat', fill: 'bg-pulse-flat', word: 'flat' },
  down: { edge: 'border-pulse-down', fill: 'bg-pulse-down', word: 'down' },
};

const LEVELS = [1, 2, 3, 4, 5] as const;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * "2026-06-15" → "Jun 15", for an axis tick.
 *
 * `week.slice(5)` gave "06-15", which is a machine's date: two numbers with a dash between them
 * that a reader has to work out the order of. The tick stays a `caption` — it IS a label — but a
 * label of a month and a day rather than of a field in a database row. It is also the ONE date
 * shape this screen uses for a week, so the axis and the note rows under it agree; a card with two
 * date formats in it is a card that was assembled rather than designed.
 */
function tickLabel(week: string | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(week ?? '');
  if (!m) return week ?? '';
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]} ${Number(m[3])}`;
}

/** The Monday before a Monday, in UTC so a timezone cannot shift a week boundary by a day. */
function previousWeek(week: string): string | null {
  const t = Date.parse(`${week}T00:00:00Z`);
  if (Number.isNaN(t)) return null;
  return new Date(t - 7 * 86_400_000).toISOString().slice(0, 10);
}

/**
 * How many weeks in an unbroken run you have logged, counted back from now.
 *
 * The open week does not count against you: if this week is still unrated the run is measured from
 * last week, because a streak that resets every Monday morning and has to be re-earned by lunchtime
 * is a streak that punishes you for the calendar. It is drawn beside the faces as the reason to
 * press one — and it is a count of check-ins, never of what any of them said, so it is not a score
 * and cannot become one.
 */
function loggedStreak(mine: { week: string }[], currentWeek: string): number {
  const have = new Set(mine.map((m) => m.week));
  let cursor: string | null = have.has(currentWeek) ? currentWeek : previousWeek(currentWeek);
  let run = 0;
  while (cursor && have.has(cursor)) {
    run++;
    cursor = previousWeek(cursor);
  }
  return run;
}

/**
 * The last `n` week-starts ending at `current`, oldest first.
 *
 * The chart needs a continuous axis and `mine` carries only the weeks that were rated, so the gaps
 * have to be reconstructed here — a week nobody rated leaves a hole in the line rather than being
 * quietly closed up, which would draw a trend through a week that never happened. The shared strip
 * runs off the same twelve slots for the same reason: it used to pack its rings up against each
 * other, so a couple who both logged in June and again in August got two rings side by side and a
 * strip that claimed a run they never had. Parsed as UTC so a timezone cannot shift a boundary.
 */
function weeksBack(current: string, n: number): string[] {
  const end = Date.parse(`${current}T00:00:00Z`);
  if (Number.isNaN(end)) return [];
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(new Date(end - i * 7 * 86_400_000).toISOString().slice(0, 10));
  return out;
}

/**
 * The one sentence over the shared strip, derived here and stored nowhere.
 *
 * The rule: compare the last 4 complete weeks against the 4 before them. `shared` arrives newest
 * first, so `slice(0, 4)` is the recent block and `slice(4, 8)` the one before it. More ups than
 * that earlier block reads as better, more downs as rougher, and anything else as steadier — ups
 * are checked first, because both can be true at once when the earlier block held more flats.
 *
 * BOTH BLOCKS HAVE TO BE FULL, and the guard was on the wrong thing: `shared.length < 2` let three
 * complete weeks print "Better than last month" off a recent block of three against an earlier
 * block of NOTHING. Counting ups in a four-slot window and comparing it with a one-slot window is
 * not a comparison, it is arithmetic that always favours the bigger window — and the sentence says
 * "last month" out loud, so it owes the reader a month on each side of it. Under that there is no
 * comparison to make, and saying one anyway would be inventing exactly the kind of claim this card
 * promises not to make.
 */
function sharedSentence(shared: { direction: Direction }[]): string {
  const recent = shared.slice(0, 4);
  const before = shared.slice(4, 8);
  if (recent.length < 4 || before.length < 4) return 'Too early to say';
  const count = (xs: { direction: Direction }[], d: Direction) => xs.filter((w) => w.direction === d).length;
  if (count(recent, 'up') > count(before, 'up')) return 'Better than last month';
  if (count(recent, 'down') > count(before, 'down')) return 'Rougher than last month';
  return 'Steadier than last month';
}

/** A hollow ring: the shared view's one mark, and it stays hollow because there is no number in it. */
function Ring({ direction, size = 18 }: { direction: Direction; size?: number }) {
  const stroke = Math.max(2, Math.round(size / 6));
  return (
    <View
      className={DIRECTIONS[direction].edge}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: stroke,
      }}
    />
  );
}

/**
 * The FIRST complete week, which has no week before it to be up or down from.
 *
 * It used to be drawn as a grey "flat" ring, which is a claim: it says the week held level, off a
 * comparison that does not exist. A dashed neutral ring is the honest mark — the strip starts here
 * — and it is dashed rather than differently coloured so it cannot be mistaken for a fourth
 * direction. The legend says what it means in words.
 */
function OpeningRing({ size = 18 }: { size?: number }) {
  const stroke = Math.max(2, Math.round(size / 6));
  return (
    <View
      className="border-muted-foreground"
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: stroke,
        borderStyle: 'dashed',
        opacity: 0.55,
      }}
    />
  );
}

/** A week only one of you logged: a hairline, so the gap keeps its place without pretending to be one. */
function EmptyWeek({ size = 18 }: { size?: number }) {
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <View className="bg-muted-foreground" style={{ width: 8, height: 2, borderRadius: 1, opacity: 0.4 }} />
    </View>
  );
}

/**
 * The legend's swatch — a SQUARE, and filled.
 *
 * It was a small copy of the ring itself, and five hollow circles in a row under a chart is the
 * universal drawing of a radio group: people read it as "pick one of these" and tried to. A filled
 * square is a key, which is what this is. The strip's marks stay rings; only the legend changes.
 */
function Swatch({ direction }: { direction: Direction }) {
  return <View className={cn(DIRECTIONS[direction].fill, 'h-3 w-3 rounded-[2px]')} />;
}

/** A tick label is 52px of centred caption; keep it inside the strip it is labelling. */
const TICK_W = 52;
const tickLeft = (centre: number, width: number) => Math.max(0, Math.min(centre - TICK_W / 2, Math.max(0, width - TICK_W)));

/**
 * Twelve weeks of shared reading, on the same twelve slots the trend chart draws.
 *
 * It MEASURES, for the reason the face scale does: twelve fixed 20px slots plus their gaps want
 * about 260px, which is more than a card has on a 320px phone, so the strip wrapped onto a second
 * line and a week-over-week reading came out as two unrelated rows of circles. Twelve `flex-1`
 * cells in a row that cannot wrap, and the ring inside each is as big as its cell allows up to the
 * 18px it wants. Not a breakpoint: this is the width of this strip in whatever column it landed in.
 */
function SharedStrip({
  strip,
  openingWeek,
}: {
  strip: { week: string; direction: Direction | null }[];
  openingWeek: string | null;
}) {
  const [width, setWidth] = React.useState(0);
  const gap = 2;
  const slot = width > 0 ? (width - gap * (strip.length - 1)) / strip.length : 0;
  const ring = slot > 0 ? Math.max(10, Math.min(18, Math.floor(slot) - 2)) : 18;
  const centreAt = (i: number) => slot * (i + 0.5) + gap * i;

  return (
    <View className="pt-1">
      <View
        className="w-full flex-row flex-nowrap items-center"
        style={{ gap }}
        onLayout={(e) => setWidth(e.nativeEvent.layout.width)}>
        {strip.map((w) => (
          <View key={w.week} className="min-w-0 flex-1 items-center">
            {w.direction === null ? (
              <EmptyWeek size={ring} />
            ) : w.week === openingWeek ? (
              <OpeningRing size={ring} />
            ) : (
              <Ring direction={w.direction} size={ring} />
            )}
          </View>
        ))}
      </View>

      {/**
       * Every fourth slot carries its week, counted back from the NEWEST so the right-hand end is
       * always dated — a strip you cannot date at the end you read first is a strip of anonymous
       * circles. The last label is nudged in off the edge rather than being clipped by the card.
       */}
      <View style={{ height: 16 }}>
        {width > 0
          ? strip.map((w, i) =>
              (strip.length - 1 - i) % 4 === 0 ? (
                <Text
                  key={`tick-${w.week}`}
                  variant="caption"
                  numberOfLines={1}
                  className="text-muted-foreground"
                  style={{
                    position: 'absolute',
                    width: TICK_W,
                    left: tickLeft(centreAt(i), width),
                    top: 3,
                    textAlign: 'center',
                  }}>
                  {tickLabel(w.week)}
                </Text>
              ) : null,
            )
          : null}
      </View>
    </View>
  );
}

export function PulseScreen() {
  const { refreshBadges, fail } = useStore();
  const me = useStore((s) => s.me);
  const { wide, expanded } = useLayout();
  const [state, setState] = React.useState<PulseState | null>(null);
  const [note, setNote] = React.useState('');
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [pending, setPending] = React.useState<number | null>(null);
  const [saving, setSaving] = React.useState(false);
  const noteTouched = React.useRef(false);
  /**
   * Whether the person has said anything about the box being open, either way.
   *
   * The screen polls every forty seconds, and "a stored note opens the box" is a rule about
   * ARRIVING at the screen, not a rule the poll gets to re-apply — without this, pressing Close on
   * a note you had already saved reopened it within the minute, which reads as the app arguing.
   */
  const noteDecided = React.useRef(false);

  const load = React.useCallback(async () => {
    const s = await api.get<PulseState>('/api/pulse');
    setState(s);
    if (!noteTouched.current && s.myThisWeek?.note) {
      setNote(s.myThisWeek.note);
      // A note that already exists is a note you came back to read: the box opens holding it.
      if (!noteDecided.current) setNoteOpen(true);
    }
  }, []);

  const { loaded, error, reload } = usePoll(load, 40_000);

  /**
   * One POST for both presses, because the route upserts on (partner, week) — and because it
   * overwrites `note` with whatever it is sent, the note in the box always rides along with a face
   * tap. Sending the rating alone would silently wipe a note that is still on screen.
   */
  const save = React.useCallback(
    async (level: number) => {
      setPending(level);
      setSaving(true);
      try {
        await api.post('/api/pulse', { rating: level, note: note.trim() || undefined });
        await Promise.all([load(), refreshBadges()]);
      } catch (e) {
        fail(e);
      } finally {
        setPending(null);
        setSaving(false);
      }
    },
    [note, load, refreshBadges, fail],
  );

  if (!loaded) return (
    <Panel id="pulse">
      <PanelSkeleton rows={3} />
    </Panel>
  );
  if (error) return (
    <Panel id="pulse">
      <LoadFailed onRetry={reload} reason={error} />
    </Panel>
  );

  if (!state) return <Panel id="pulse">{null}</Panel>;

  const logged = pending ?? state.myThisWeek?.rating ?? null;
  const chosen = logged;
  const stored = state.myThisWeek?.note ?? '';
  const them = me?.partnerName ?? 'Your partner';
  const streak = loggedStreak(state.mine, state.currentWeek);

  const faceSize = wide ? 60 : 52;
  const chartHeight = expanded ? 150 : wide ? 132 : 112;

  const weeks = weeksBack(state.currentWeek, 12);
  const byWeek = new Map(state.mine.map((m) => [m.week, m]));
  const points = weeks.map((week) => {
    const row = byWeek.get(week);
    return { week, rating: row?.rating ?? null, note: row?.note ?? null };
  });

  /**
   * The shared strip on the SAME twelve slots as the trend, so a gap is a gap on both.
   *
   * `shared` carries only the complete weeks and arrives newest first; its last entry is therefore
   * the earliest complete week the server sent, and that one is the opening ring — the one with
   * nothing before it. Where the couple has more than twelve weeks of history the opener falls off
   * the left of this window and simply is not drawn, which is right: it is only interesting while
   * the strip is still short enough to contain its own beginning.
   */
  const sharedByWeek = new Map(state.shared.map((w) => [w.week, w.direction as Direction]));
  const openingWeek = state.shared.length ? state.shared[state.shared.length - 1]!.week : null;
  const strip = weeks.map((week) => ({ week, direction: sharedByWeek.get(week) ?? null }));
  const noted = state.mine.filter((m) => m.note).slice(0, 4);

  const checkIn = (
    <Card>
      <CardContent>
        <CardHead>This week, honestly</CardHead>
        <CardDescription>
          Only you ever see your face and your note. The shared view is direction only.
        </CardDescription>

        {/* The tap is the log. No Save button for the RATING — a check-in that costs two presses
            gets skipped. The note is this screen's, because this screen is what saves it. */}
        <FaceScale
          value={chosen}
          onChange={save}
          size={faceSize}
          disabled={saving}
          aside={
            streak > 0 ? (
              <Text variant="small" className="text-muted-foreground">
                Weeks logged in a row · {streak}
              </Text>
            ) : null
          }>
          {noteOpen ? (
            <View className="gap-2">
              <Textarea
                value={note}
                onChangeText={(v) => {
                  noteTouched.current = true;
                  setNote(v);
                }}
                placeholder="Private note to future-you (optional)"
              />
              {/* A BOX WITH NO BUTTON READS AS BROKEN. The save is always drawn and always in the
                  same place; before a face is tapped it is off and says why, rather than appearing
                  from nowhere the moment the two conditions it never announced both came true. */}
              <View className="flex-row flex-wrap items-start gap-2">
                <Button
                  size="sm"
                  disabled={saving || logged === null}
                  reason={logged === null ? 'Tap a face first — the note saves with it.' : undefined}
                  onPress={() => (logged === null ? undefined : save(logged))}>
                  <Text>Save note</Text>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  flush={false}
                  disabled={saving}
                  onPress={() => {
                    noteTouched.current = false;
                    noteDecided.current = true;
                    setNote(stored);
                    setNoteOpen(false);
                  }}>
                  <Text>Close</Text>
                </Button>
              </View>
            </View>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="self-start"
              onPress={() => {
                noteDecided.current = true;
                setNoteOpen(true);
              }}>
              <Text>{stored ? 'Edit your note' : 'Add a note'}</Text>
            </Button>
          )}
        </FaceScale>

        {/**
         * WHETHER, never what. The shared ring is missing until you have both logged, and a card
         * that will not say why reads as broken — so it says the one fact it is allowed to know.
         * The second half is the game speaking about its own rules; nobody here is waiting on
         * anybody, and no copy on this screen may frame it that way.
         */}
        <Text variant="small" className={cn('text-muted-foreground', MEASURE_CLASS)}>
          {state.partnerLoggedThisWeek ? `${them} has logged this week.` : `${them} hasn’t logged yet.`} The ring lands
          once you have both logged.
        </Text>
      </CardContent>
    </Card>
  );

  const sharedCard = state.shared.length ? (
    <Card>
      <CardContent>
        <CardHead>Us, week over week</CardHead>
        <CardDescription>Both of you rated it, so one ring. No numbers, on purpose.</CardDescription>

        {/* The sentence sits above its rings, the way MindDoc states the reading over the chart. */}
        <Text variant="heading">{sharedSentence(state.shared)}</Text>

        <SharedStrip strip={strip} openingWeek={openingWeek} />

        <View className="flex-row flex-wrap items-center gap-3">
          {(['up', 'flat', 'down'] as const).map((d) => (
            <View key={d} className="flex-row items-center gap-1.5">
              <Swatch direction={d} />
              <Text variant="small" className="text-muted-foreground">
                {DIRECTIONS[d].word}
              </Text>
            </View>
          ))}
        </View>

        <Text variant="note" className={MEASURE_CLASS}>
          A week only one of you logged stays empty, and the dashed ring is the first week — there
          is nothing before it to compare with.
        </Text>
      </CardContent>
    </Card>
  ) : null;

  return (
    <Panel id="pulse">
      {/**
       * At bp-wide the check-in and the shared reading sit side by side inside the 880 column the
       * panel already caps, because both are short cards and a desktop was drawing each of them as
       * an 877px sheet with its content in the left third. The trend spans the full width below
       * them: it is a chart, and a chart is the one thing here that wants the pixels.
       *
       * `wide` is `useLayout`'s answer and not a pixel — this file may not know a breakpoint.
       */}
      <View className={cn('gap-3', wide && 'flex-row items-start')}>
        <View className={cn('min-w-0 gap-3', wide && 'flex-1')}>
          {checkIn}
          {/* ONE at a time, and under the thing the tab is for. */}
          {state.landing?.length ? (
            <DidItLand
              key={state.landing[0]!.signalId}
              entry={state.landing[0]!}
              total={state.landing.length}
              onAnswered={load}
            />
          ) : null}
        </View>
        {sharedCard ? <View className={cn('min-w-0 gap-3', wide && 'flex-1')}>{sharedCard}</View> : null}
      </View>

      {state.mine.length ? (
        <Card>
          <CardContent>
            <CardHead>Your private trend</CardHead>
            <CardDescription>Your last twelve weeks. A week you skipped leaves a gap, not a guess.</CardDescription>
            <TrendChart points={points} height={chartHeight} />
            {noted.length ? (
              <>
                <Text variant="note" className={MEASURE_CLASS}>
                  A ringed point is a week you wrote something on.
                </Text>
                {noted.map((m) => (
                  <View key={m.week} className="flex-row items-center gap-2">
                    <View style={{ width: 22, alignItems: 'center' }}>
                      <NotedDot level={m.rating} />
                    </View>
                    <Text variant="small" numberOfLines={2} className="text-muted-foreground min-w-0 flex-1">
                      {tickLabel(m.week)} — {faceFor(m.rating)?.word ?? ''} · “{m.note}”
                    </Text>
                  </View>
                ))}
              </>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </Panel>
  );
}

/** The chart's "this one has a note" mark, repeated in the list so the two are visibly one thing. */
function NotedDot({ level, size = 8 }: { level: number; size?: number }) {
  const halo = size + 8;
  return (
    <View style={{ width: halo, height: halo, alignItems: 'center', justifyContent: 'center' }}>
      <View
        className={faceFor(level)?.edge}
        style={{ position: 'absolute', width: halo, height: halo, borderRadius: halo / 2, borderWidth: 1.5, opacity: 0.7 }}
      />
      <View className={faceFor(level)?.tone} style={{ width: size, height: size, borderRadius: size / 2 }} />
    </View>
  );
}

/**
 * My weeks as a line, with the face scale standing in for the y-axis.
 *
 * The plot measures itself rather than being told a width, because it sits in a card that is a
 * different width on a phone, on a tablet and beside a detail pane. A segment is a thin View
 * rotated about its own centre between two points — there is no SVG in this app, and one is not
 * worth taking for twelve dots.
 *
 * IT NEEDED TO LOOK LIKE A CHART. One dot floating in a white rectangle with a hairline down its
 * left side is not a reading of anything: there was no telling which week the dot was, how many
 * weeks the space held, or where the bottom of the scale was — and at night the hairline was the
 * only ink on the card, so the plot disappeared entirely. Three things fix it and all three are
 * furniture rather than data: a frame in `--border` (whose bottom edge IS the baseline), a faint
 * gridline standing on every one of the twelve week slots so the emptiness is countable, and a
 * dated tick every fourth week counted back from the newest.
 */
function TrendChart({
  points,
  height,
}: {
  points: { week: string; rating: number | null; note: string | null }[];
  height: number;
}) {
  const [width, setWidth] = React.useState(0);
  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  const dot = 8;
  const stroke = 2;
  const pad = dot / 2 + 1;
  const xAt = (i: number) => (points.length < 2 ? width / 2 : pad + (i * (width - pad * 2)) / (points.length - 1));
  const yAt = (rating: number) => pad + ((5 - rating) * (height - pad * 2)) / 4;

  const segments: { key: string; left: number; top: number; length: number; angle: number }[] = [];
  if (width > 0) {
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i];
      const b = points[i + 1];
      // A missing week breaks the line: two ends are joined only when both of them exist.
      if (!a?.rating || !b?.rating) continue;
      const x1 = xAt(i);
      const y1 = yAt(a.rating);
      const x2 = xAt(i + 1);
      const y2 = yAt(b.rating);
      const length = Math.hypot(x2 - x1, y2 - y1);
      segments.push({
        key: a.week,
        left: (x1 + x2) / 2 - length / 2,
        top: (y1 + y2) / 2 - stroke / 2,
        length,
        angle: (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI,
      });
    }
  }

  const anyRated = points.some((p) => p.rating !== null);

  return (
    <View className="flex-row items-start gap-2">
      <View style={{ width: 16, height }}>
        {LEVELS.map((l) => (
          <View key={l} style={{ position: 'absolute', top: yAt(l) - 8, left: 0 }}>
            <Face level={l} size={16} />
          </View>
        ))}
      </View>

      <View className="min-w-0 flex-1">
        <View className="border-border rounded-sm border" style={{ height }} onLayout={onLayout}>
          {/* One hairline per week slot: twelve of them, so an empty stretch is a countable number
              of weeks rather than an unmeasured expanse of card. */}
          {width > 0
            ? points.map((p, i) => (
                <View
                  key={`grid-${p.week}`}
                  className="bg-border"
                  style={{ position: 'absolute', left: xAt(i), top: 0, width: 1, height, opacity: 0.5 }}
                />
              ))
            : null}

          {segments.map((s) => (
            <View
              key={s.key}
              className="bg-muted-foreground"
              style={{
                position: 'absolute',
                left: s.left,
                top: s.top,
                width: s.length,
                height: stroke,
                borderRadius: stroke / 2,
                opacity: 0.5,
                transform: [{ rotate: `${s.angle}deg` }],
              }}
            />
          ))}

          {points.map((p, i) =>
            p.rating === null ? null : p.note ? (
              <View
                key={p.week}
                style={{ position: 'absolute', left: xAt(i) - (dot + 8) / 2, top: yAt(p.rating) - (dot + 8) / 2 }}>
                <NotedDot level={p.rating} size={dot} />
              </View>
            ) : (
              <View
                key={p.week}
                className={faceFor(p.rating)?.tone}
                style={{
                  position: 'absolute',
                  left: xAt(i) - dot / 2,
                  top: yAt(p.rating) - dot / 2,
                  width: dot,
                  height: dot,
                  borderRadius: dot / 2,
                }}
              />
            ),
          )}

          {anyRated ? null : (
            <View style={{ position: 'absolute', left: 8, top: height / 2 - 10 }}>
              <Text variant="small" className="text-muted-foreground">
                Nothing logged in the last twelve weeks.
              </Text>
            </View>
          )}
        </View>

        {/* The ticks stand under their own gridlines rather than at the two far corners, which is
            what makes the twelve slots readable as weeks instead of as one long empty gap. */}
        <View style={{ height: 16 }}>
          {width > 0
            ? points.map((p, i) =>
                (points.length - 1 - i) % 4 === 0 ? (
                  <Text
                    key={`tick-${p.week}`}
                    variant="caption"
                    numberOfLines={1}
                    className="text-muted-foreground"
                    style={{
                      position: 'absolute',
                      width: TICK_W,
                      left: tickLeft(xAt(i), width),
                      top: 3,
                      textAlign: 'center',
                    }}>
                    {tickLabel(p.week)}
                  </Text>
                ) : null,
              )
            : null}
        </View>
      </View>
    </View>
  );
}

/** How long "Recorded" stays on screen before the card goes. Long enough to be read as an answer. */
const RECORDED_HOLD_MS = 900;

/**
 * "Did it land?" — asked once, two weeks after a plan finished, and reported to nobody.
 *
 * It is the only feedback the app ever gets about whether the game changed anything or only hid
 * the ask, and it belongs on Pulse because Pulse is already the private, reflective tab. Asking it
 * on Signals would put it next to the machinery, which is what it is NOT about.
 *
 * THE ACKNOWLEDGEMENT IS HELD. Answering used to refetch immediately, so "Recorded" appeared and
 * the card unmounted underneath it in the same breath — a flash where a confirmation should be.
 * The card waits `RECORDED_HOLD_MS` with the answer on screen and only then asks the screen to
 * reload, which is also what makes the "1 of 3" queue readable: one card leaves, the next arrives.
 */
function DidItLand({
  entry,
  total,
  onAnswered,
}: {
  entry: { signalId: string; kind: string; excerpt: string; asked: string; since: string };
  /** How many are due in total, so the card can say it is one of a queue rather than the only one. */
  total: number;
  onAnswered: () => Promise<void>;
}) {
  const { fail } = useStore();
  const look = useStore((s) => s.look);
  const [picked, setPicked] = React.useState<'yes' | 'bit' | 'no' | null>(null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // The hold outlives the press but must not outlive the card: a refetch fired into an unmounted
  // screen is a state update nobody is listening for.
  React.useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const answer = async (value: 'yes' | 'bit' | 'no') => {
    setPicked(value);
    try {
      await api.post(`/api/signals/${entry.signalId}/landed`, { answer: value });
      timer.current = setTimeout(() => {
        void onAnswered();
      }, RECORDED_HOLD_MS);
    } catch (e) {
      setPicked(null);
      fail(e);
    }
  };

  const answered = picked !== null;

  return (
    <Card className={look === 'tuxedo' ? 'border-gold' : 'border-primary'}>
      <CardContent>
        <CardHead row>
          <Text variant="heading" className="min-w-0 flex-1">
            Did it land?
          </Text>
          <Badge variant="band">
            <Text>private</Text>
          </Badge>
        </CardHead>
        {answered ? (
          <Text>Recorded — nothing more is asked about this signal.</Text>
        ) : (
          <>
            {total > 1 ? (
              <Text variant="note">1 of {total} · the rest come one at a time.</Text>
            ) : null}
            <Text className={MEASURE_CLASS}>
              Your {entry.kind} from {agoLabel(localDay(entry.asked))} finished {agoLabel(localDay(entry.since))}. Did
              anything change off-screen?
            </Text>
            {/* Your own words, and only ever yours — clamped to two lines, because a signal can be
                a paragraph and this card is a question rather than a transcript. */}
            <Text variant="small" numberOfLines={2} className={cn('text-muted-foreground', MEASURE_CLASS)}>
              “{entry.excerpt}”
            </Text>
            {/* ONE choice set, one weight. Three buttons at three different weights read as a
                recommendation — the filled one was "Yes" — and the bare "No" fell to 2.76:1 in the
                dark rooms and read as disabled. Three equal outlines; the one you pick fills. */}
            <View className="flex-row gap-2">
              {([
                ['yes', 'Yes'],
                ['bit', 'A bit'],
                ['no', 'No'],
              ] as const).map(([value, label]) => (
                <Button
                  key={value}
                  variant={picked === value ? 'default' : 'outline'}
                  className="min-w-0 flex-1"
                  onPress={() => answer(value)}>
                  <Text numberOfLines={1}>{label}</Text>
                </Button>
              ))}
            </View>
            <Text variant="small" className={cn('text-muted-foreground', MEASURE_CLASS)}>
              Reported to nobody. It is how the app knows whether the game changed anything, or only hid the ask.
            </Text>
          </>
        )}
      </CardContent>
    </Card>
  );
}
